import React, { useEffect, useState, useMemo } from "react";
import "../styles/executive-responsive.css";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";

import { DateCarousel } from "../components/DateCarousel";

import { getParticipantColor } from "../../../utils/participantColors";

interface Participant {
  id: string;
  name: string;
  alias: string;
  avatarUrl: string;
  order: number;
  status: string;
  role: string;
  colorId: string;
  customColor: string;
  updatedAt?: string;
}

interface Props {
  projectId: string;
  onSelect: (participant: Participant) => void;
  /** Which summary type to show in preview cards (default: "lifetime") */
  summaryType?: "lifetime" | "daily";
}

const PAGE_SIZE = 100;

export const participantsCache: Record<string, Participant[]> = {};

export const ParticipantList: React.FC<Props> = ({
  projectId,
  onSelect,
  summaryType: _summaryType = "lifetime",
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [participants, setParticipants] = useState<Participant[]>(
    participantsCache[projectId] || [],
  );
  const [loading, setLoading] = useState(!participantsCache[projectId]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedDate = searchParams.get("date");
  const setSelectedDate = (date: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (date) next.set("date", date);
        else next.delete("date");
        return next;
      },
      { replace: true },
    );
  };

  // Date navigation
  const recordingDays = useMemo(() => {
    const days = [];
    let current = new Date("2026-05-17T12:00:00-06:00");
    const todayMXString = new Date().toLocaleString("en-US", { timeZone: "America/Mexico_City" });
    const todayMXDate = new Date(todayMXString);
    todayMXDate.setHours(23, 59, 59, 999);

    let dayNumber = 1;
    while (current <= todayMXDate) {
      const mxDateString = current
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      const todayIsoMX = todayMXDate
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      days.push({
        iso: mxDateString,
        date: new Date(current),
        isToday: mxDateString === todayIsoMX,
        dayNumber,
      });
      current.setDate(current.getDate() + 1);
      dayNumber++;
    }
    return days;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!participantsCache[projectId]) setLoading(true);
        setError(null);
        const res = await authFetch(`${config.API_URL}/api/projects/${projectId}/participants`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        const list: Participant[] = body.participants || [];
        participantsCache[projectId] = list;
        setParticipants(list);
      } catch (e: any) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Filter by search
  // Filter and sort by search + groups
  const filteredParticipants = useMemo(() => {
    let list = participants;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || (p.alias && p.alias.toLowerCase().includes(q)),
      );
    }

    const getGroupRank = (p: Participant) => {
      let isDead = p.status === "eliminated";
      if (isDead && selectedDate && p.updatedAt) {
        if (new Date(selectedDate).getTime() < new Date(p.updatedAt).getTime()) {
          isDead = false;
        }
      }
      if (isDead) return 5;
      if (p.role === "host" || p.name.toUpperCase().includes("PRESENTADORA CLAUDIA")) return 4;

      const isMaestro =
        p.name.toUpperCase().includes("MAESTR") ||
        p.alias?.toUpperCase().includes("MAESTR") ||
        p.alias?.startsWith("M ");

      if (p.role === "judge") {
        return isMaestro ? 3 : 2;
      }

      return 1; // Active contestant
    };

    return [...list].sort((a, b) => {
      const rankA = getGroupRank(a);
      const rankB = getGroupRank(b);
      if (rankA !== rankB) return rankA - rankB;

      // If both are eliminated, sort by elimination time descending (latest first)
      if (rankA === 5 && rankB === 5) {
        const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return timeB - timeA;
      }

      // If same group and not eliminated, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }, [participants, searchQuery]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredParticipants.length / PAGE_SIZE));
  const paginatedParticipants = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredParticipants.slice(start, start + PAGE_SIZE);
  }, [filteredParticipants, currentPage]);

  if (loading) {
    return (
      <div style={{ padding: 40, color: "var(--color-text-muted)", textAlign: "center" }}>
        {t("executive.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, color: "var(--color-accent-danger)", textAlign: "center" }}>
        {error}
      </div>
    );
  }

  if (!participants.length) {
    return (
      <div style={{ padding: 40, color: "var(--color-text-muted)", textAlign: "center" }}>
        {t("executive.emptyParticipants")}
      </div>
    );
  }

  return (
    <div
      className="participant-list-container"
      style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}
    >
      {/* Date Navigation Carousel */}
      <DateCarousel
        recordingDays={recordingDays}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
      />

      {/* Header with count + search */}
      <div
        className="participant-list-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "0.9rem",
            letterSpacing: "2px",
            color: "var(--color-text-muted)",
            textTransform: "uppercase",
            flexShrink: 0,
          }}
        >
          {t("executive.participantsTitle", { count: filteredParticipants.length })}
          {searchQuery && participants.length !== filteredParticipants.length && (
            <span style={{ fontSize: "0.75rem", opacity: 0.6, marginLeft: 8 }}>
              / {participants.length} total
            </span>
          )}
        </h2>
        <input
          className="participant-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("common.search", "Buscar...") as string}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "var(--border-radius-md, 8px)",
            padding: "8px 14px",
            color: "var(--color-text-primary)",
            fontSize: "0.8rem",
            outline: "none",
            minWidth: 200,
            maxWidth: 300,
            fontFamily: "inherit",
          }}
        />
      </div>

      {/* Grid of participant cards */}
      <div
        className="participant-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {paginatedParticipants.map((p) => {
          let isDeadToday = p.status === "eliminated";

          if (isDeadToday && selectedDate && p.updatedAt) {
            // Comparar selectedDate con updatedAt
            const selectedTime = new Date(selectedDate).getTime();
            const eliminationTime = new Date(p.updatedAt).getTime();
            if (selectedTime < eliminationTime) {
              isDeadToday = false;
            }
          }

          const pColor = getParticipantColor(p.colorId, p.customColor);

          return (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              style={{
                textAlign: "left",
                background: isDeadToday ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.03)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "var(--border-radius-md)",
                padding: 16,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                color: isDeadToday ? "var(--color-text-muted)" : "inherit",
                fontFamily: "inherit",
                transition: "border-color 0.2s, transform 0.2s",
                opacity: isDeadToday ? 0.6 : 1,
                filter: isDeadToday ? "grayscale(100%)" : "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = isDeadToday
                  ? "var(--color-text-muted)"
                  : pColor.border;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-panel-border)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "#222",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${isDeadToday ? "#555" : pColor.border}`,
                  }}
                >
                  {p.avatarUrl ? (
                    <img
                      src={p.avatarUrl}
                      alt={p.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <i
                      className="bx bx-user"
                      style={{ fontSize: 24, color: isDeadToday ? "#555" : pColor.bg }}
                    ></i>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      color: isDeadToday ? "inherit" : pColor.bg,
                    }}
                  >
                    {p.name}
                    {isDeadToday && <span title="Eliminado">💀</span>}
                  </span>
                  {p.alias && (
                    <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                      "{p.alias}"
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div
          className="pagination-controls"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 24,
            paddingBottom: 16,
          }}
        >
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              background: currentPage === 1 ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "var(--border-radius-md, 8px)",
              padding: "8px 16px",
              color: currentPage === 1 ? "var(--color-text-muted)" : "var(--color-text-primary)",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
              fontFamily: "inherit",
              opacity: currentPage === 1 ? 0.4 : 1,
              transition: "all 0.2s",
            }}
          >
            ← {t("common.previous", "Anterior")}
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                background:
                  page === currentPage
                    ? "linear-gradient(135deg, #EC4899, #DB2777)"
                    : "rgba(255,255,255,0.04)",
                border: `1px solid ${page === currentPage ? "transparent" : "rgba(255,255,255,0.1)"}`,
                borderRadius: "var(--border-radius-md, 8px)",
                padding: "8px 14px",
                color: page === currentPage ? "#fff" : "var(--color-text-muted)",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: page === currentPage ? 700 : 500,
                fontFamily: "inherit",
                minWidth: 36,
                transition: "all 0.2s",
                boxShadow: page === currentPage ? "0 2px 12px rgba(236,72,153,0.25)" : "none",
              }}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              background:
                currentPage === totalPages ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "var(--border-radius-md, 8px)",
              padding: "8px 16px",
              color:
                currentPage === totalPages
                  ? "var(--color-text-muted)"
                  : "var(--color-text-primary)",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
              fontFamily: "inherit",
              opacity: currentPage === totalPages ? 0.4 : 1,
              transition: "all 0.2s",
            }}
          >
            {t("common.next", "Siguiente")} →
          </button>
        </div>
      )}

      {/* No results message when searching */}
      {filteredParticipants.length === 0 && searchQuery && (
        <div
          style={{
            padding: 40,
            color: "var(--color-text-muted)",
            textAlign: "center",
            fontSize: "0.85rem",
          }}
        >
          {t("common.noResults", "No se encontraron resultados para")}{" "}
          <strong>"{searchQuery}"</strong>
        </div>
      )}
    </div>
  );
};
